import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DownloadOutlined,
  ArticleOutlined,
  PrivacyTipOutlined,
  SecurityOutlined,
  GavelOutlined,
  VisibilityOutlined,
  Close,
} from '@mui/icons-material';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

const S3_PDF_URL =
  'https://eltimex.s3.eu-central-1.amazonaws.com/terms%26conditions/Regulamin+sklepu+internetowego+ELTIMEX+(1).pdf';

const RODO_PDF_URL =
  'https://eltimex.s3.eu-central-1.amazonaws.com/terms%26conditions/Informacja++RODO+Eltimex.pdf';

const POLITYKA_PRYWATNOSCI_PDF_URL =
  'https://eltimex.s3.eu-central-1.amazonaws.com/terms%26conditions/Polityka+prywatnos%CC%81ci+Eltimex.pdf';

const WNIOSEK_O_UDOSTEPNIENIU_DANYCH_OSOBOWYCH_PDF_URL =
  'https://eltimex.s3.eu-central-1.amazonaws.com/terms%26conditions/wniosek+o+udoste%CC%A8pnienie+danych.pdf';

const DOCUMENTS = [
  {
    name: 'Regulamin sklepu internetowego',
    url: S3_PDF_URL,
    icon: <GavelOutlined className="text-primary text-2xl" />,
    description:
      'Zasady korzystania ze sklepu, realizacji zamówień i reklamacji.',
  },
  {
    name: 'Informacja RODO',
    url: RODO_PDF_URL,
    icon: <PrivacyTipOutlined className="text-primary text-2xl" />,
    description:
      'Informacje dotyczące przetwarzania danych osobowych zgodnie z RODO.',
  },
  {
    name: 'Wniosek o udostępnienie danych osobowych',
    url: WNIOSEK_O_UDOSTEPNIENIU_DANYCH_OSOBOWYCH_PDF_URL,
    icon: <ArticleOutlined className="text-primary text-2xl" />,
    description:
      'Formularz umożliwiający uzyskanie informacji o przetwarzanych danych.',
  },
  {
    name: 'Polityka prywatności',
    url: POLITYKA_PRYWATNOSCI_PDF_URL,
    icon: <SecurityOutlined className="text-primary text-2xl" />,
    description:
      'Szczegółowe zasady ochrony i przetwarzania danych osobowych.',
  },
];

const TermsAndConditions: React.FC = () => {
  const config = useConfig();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (document: { name: string; url: string }) => {
    setSelectedDocument(document);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Nagłówek strony */}
        <div className="mb-10">
          <motion.h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Regulamin i Polityka Prywatności
          </motion.h1>
          <motion.p
            className="text-gray-600 text-lg max-w-3xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Zapoznaj się z zasadami korzystania z naszego sklepu oraz sposobem
            przetwarzania danych osobowych.
          </motion.p>
        </div>

        {/* Sekcja z dokumentami */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {DOCUMENTS.map((doc, idx) => (
            <div
              key={doc.name}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col sm:flex-row items-center gap-4"
            >
              <div className="flex-shrink-0">{doc.icon}</div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{doc.name}</h3>
                <p className="text-gray-600 text-sm mb-2">{doc.description}</p>
              </div>
              <div className="flex gap-2">
                <motion.button
                  onClick={() => openModal({ name: doc.name, url: doc.url })}
                  className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <VisibilityOutlined fontSize="small" />
                  <span>Zobacz</span>
                </motion.button>
                <motion.a
                  href={doc.url}
                  download
                  className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors text-sm font-medium"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <DownloadOutlined fontSize="small" />
                  <span>Pobierz</span>
                </motion.a>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Dodatkowe informacje */}
        <motion.div
          className="mt-8 text-center text-gray-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p>Ostatnia aktualizacja: 29.06.2025</p>
          <p className="mt-2">
            W przypadku pytań dotyczących regulaminu lub polityki prywatności,
            prosimy o kontakt z naszym działem obsługi klienta.
          </p>
        </motion.div>
      </div>

      {/* Modal z PDF */}
      {isModalOpen && selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeModal}></div>
          <motion.div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] m-4 flex flex-col"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedDocument.name}
              </h3>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Close />
              </button>
            </div>

            {/* PDF Content */}
            <div className="flex-1 relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
              )}
              <iframe
                src={selectedDocument.url}
                className="w-full h-full"
                title={selectedDocument.name}
                onLoad={() => setIsLoading(false)}
              />
            </div>
          </motion.div>
        </div>
      )}

      <Seo
        title={`Regulamin, Polityka Prywatności`}
        description="Regulamin i Polityka Prywatności firmy"
      ></Seo>
    </>
  );
};

export default TermsAndConditions;
